import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { Projectile } from "../entities/Projectile.ts";
import { isStealthHidden } from "./StealthGuard.ts";
import { COMBAT } from "../core/config.ts";

/**
 * Pure targeting functions for the combat system.
 * No side effects — returns targets based on proximity.
 */

/** Find the closest active enemy within range of a tower.
 *  LD-22.1-1: Unrevealed stealth enemies are **completely skipped** by all
 *  towers — they cannot be targeted until a Signal tower's `detectStealth`
 *  radius reveals them (updateStealthReveal in BeamCombat.ts). Reveal is
 *  dynamic (LD-22.1-3): leaving Signal range re-hides them.
 *  Supersedes LD-22-7's 50%-damage+priority-penalty compromise — that
 *  was Audit C2's mitigation for a "draft-RNG fail state" worry which
 *  is now addressed by LD-22.1-2 (pre-wave warning) instead. */
export function findTarget(
  tower: Tower,
  enemies: Enemy[],
  effectiveRange?: number,
  preferMarked = false,
  targetFastest = false,
  hasLos?: (enemy: Enemy) => boolean,
): Enemy | null {
  let closest: Enemy | null = null;
  let closestDistSq = Infinity;
  let markedTarget: Enemy | null = null;
  let markedDistSq = Infinity;
  let fastestTarget: Enemy | null = null;
  let fastestSpeed = -1;
  const range = effectiveRange ?? tower.range;
  const rangeSq = range * range;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    // LD-22.1-1: skip unrevealed stealth entirely — towers literally
    // cannot "see" them without Signal proximity.
    if (isStealthHidden(enemy)) continue;

    const dx = tower.x - enemy.x;
    const dy = tower.y - enemy.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > rangeSq) continue;
    if (hasLos && !hasLos(enemy)) continue;

    if (distSq < closestDistSq) {
      closest = enemy;
      closestDistSq = distSq;
    }
    if (preferMarked && enemy.marked && distSq < markedDistSq) {
      markedTarget = enemy;
      markedDistSq = distSq;
    }
    if (targetFastest && enemy.speed > fastestSpeed) {
      fastestTarget = enemy;
      fastestSpeed = enemy.speed;
    }
  }
  if (preferMarked && markedTarget) return markedTarget;
  if (targetFastest && fastestTarget) return fastestTarget;
  return closest;
}

/** Find next pierce target near a projectile, excluding original. */
export function findPierceTarget(
  proj: Projectile,
  enemies: Enemy[],
): Enemy | null {
  let closest: Enemy | null = null;
  let closestDistSq = Infinity;
  const maxRangeSq = COMBAT.PIERCE_RANGE * COMBAT.PIERCE_RANGE;

  for (const enemy of enemies) {
    if (!enemy.active || enemy === proj.target) continue;
    if (isStealthHidden(enemy)) continue;
    if (proj.hitEnemyVersions.has(enemy.version)) continue;
    const dx = proj.x - enemy.x;
    const dy = proj.y - enemy.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < maxRangeSq && distSq < closestDistSq) {
      closest = enemy;
      closestDistSq = distSq;
    }
  }
  return closest;
}

/** Pick the highest-HP active enemy within range that has a clear line of sight.
 *  Stealth-hidden enemies are skipped. Tie-break keeps first encountered.
 *  If preferMarked is true, the highest-HP marked target is preferred; falls
 *  back to highest-HP overall if no marked target is in range. */
export function findHighestHpTargetWithLos(
  tower: Tower,
  enemies: Enemy[],
  effectiveRange: number,
  hasLos: (enemy: Enemy) => boolean,
  preferMarked = false,
): Enemy | null {
  const rangeSq = effectiveRange * effectiveRange;
  let best: Enemy | null = null;
  let bestHp = -Infinity;
  let bestMarked: Enemy | null = null;
  let bestMarkedHp = -Infinity;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;

    const dx = tower.x - enemy.x;
    const dy = tower.y - enemy.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > rangeSq) continue;
    if (!hasLos(enemy)) continue;

    if (enemy.hp > bestHp) {
      best = enemy;
      bestHp = enemy.hp;
    }
    if (preferMarked && enemy.marked && enemy.hp > bestMarkedHp) {
      bestMarked = enemy;
      bestMarkedHp = enemy.hp;
    }
  }

  if (preferMarked && bestMarked) return bestMarked;
  return best;
}

/** Find a ground aim point that covers the densest enemy cluster in radius.
 *
 *  PT-01 note: the dominant cost of this scan in the live game is calling it
 *  every frame regardless of cooldown. The fix is cooldown-gating at the call
 *  site (updateVirusTower) — an in-browser profile showed a SpatialGrid here is
 *  a net regression because the zone radius covers most of the in-range crowd,
 *  so a grid prunes nothing while paying rebuild+sort overhead. See D-122. */
export function findClusterTarget(
  tower: Tower,
  enemies: Enemy[],
  effectiveRange: number,
  zoneRadius: number,
): { x: number; y: number } | null {
  const rangeSq = effectiveRange * effectiveRange;
  const zoneSq = zoneRadius * zoneRadius;

  let bestEnemy: Enemy | null = null;
  let bestCount = 0;

  for (const center of enemies) {
    if (!center.active) continue;
    if (isStealthHidden(center)) continue;

    const dxTower = tower.x - center.x;
    const dyTower = tower.y - center.y;
    if (dxTower * dxTower + dyTower * dyTower > rangeSq) continue;

    let count = 0;
    for (const enemy of enemies) {
      if (!enemy.active) continue;
      if (isStealthHidden(enemy)) continue;
      const dx = enemy.x - center.x;
      const dy = enemy.y - center.y;
      if (dx * dx + dy * dy <= zoneSq) count++;
    }

    if (count > bestCount) {
      bestCount = count;
      bestEnemy = center;
      continue;
    }

    if (
      count === bestCount &&
      bestEnemy &&
      center.pathIndex > bestEnemy.pathIndex
    ) {
      bestEnemy = center;
    }
  }

  if (!bestEnemy) return null;
  return { x: bestEnemy.x, y: bestEnemy.y };
}
